import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import cn from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import { Typography, Button, Box } from '@mui/material';
import { useFetchAppointmentDetail, useAppointmentActions, useOrganization, useAuth } from 'hooks';
import { CheckMarkIcon, CameraIcon } from 'components/elements/Icons';
import { DetailControls } from 'components/elements';
import {
  AppointmentDetail,
  AppointmentVirtualAddress,
  AppointmentDetailInstruction,
  AppointmentLocationMap,
  AppointmentAttachments,
  AppointmentInvoices,
} from 'components/modules';
import { dateUtils } from 'utils';
import { routes, virtualTypes, timeWindows } from 'const';
import Styles from './styles';

const useStyles = makeStyles(Styles);

function AppointmentDetails() {
  const { id } = useParams();
  const { data, relatedData, isLoading } = useFetchAppointmentDetail(id);
  const { joinOnlineAppointment, confirmAppointment } = useAppointmentActions(id);
  const { t } = useTranslation('labels');
  const { id: userId } = useAuth();
  const { data: organization } = useOrganization();
  const classes = useStyles();
  const isOnline = data?.online;
  const isHosted = relatedData?.location?.attributes?.virtualType === virtualTypes.hosted;
  const isPastAppointment = useMemo(() => dateUtils.isDateBeforeNow(data?.endDateTime), [data?.endDateTime]);

  const showMap = useMemo(() => data && data.locationAddress && ((isOnline && isHosted) || !isOnline), [data, isOnline, isHosted]);
  const displayJoinButton = useMemo(
    () => data?.online && dateUtils.isInTimeWindow(data?.startDateTime, data?.endDateTime, organization?.videoJoinWindow, organization?.videoJoinWindow),
    [data, organization?.videoJoinWindow],
  );
  const displayConfirmButton = useMemo(() => dateUtils.isInTimeWindow(data?.startDateTime, data?.endDateTime, timeWindows.appointmentConfirmation, 0), [data]);

  const isConfirmed = data?.participants?.some(({ id: participantId, isConfirmed: participantConfirmed }) => participantId === userId && participantConfirmed);

  return (
    <Box className={classes.appointmentWrapper}>
      <DetailControls backButtonRoute={routes.appointments}>
        {!isPastAppointment && displayJoinButton && (
          <Button className={classes.appointmentJoinButton} onClick={joinOnlineAppointment} variant="contained">
            <CameraIcon classes={classes.joinButtonIcon} />
            <Typography className={classes.controlButtonText} variant="caption" component="span">
              {t('join')}
            </Typography>
          </Button>
        )}
        {!isPastAppointment && displayConfirmButton && (
          <Button
            className={cn(classes.appointmentConfirmButton, { [classes.appointmentConfirmedButton]: isConfirmed })}
            onClick={confirmAppointment}
            variant="outlined"
            color="primary"
          >
            <CheckMarkIcon classes={classes.confirmButtonIcon} />
            <Typography className={classes.controlButtonText} variant="caption" component="span">
              {t(`${isConfirmed ? 'confirmed' : 'confirm'}`)}
            </Typography>
          </Button>
        )}
      </DetailControls>
      <div className={classes.appointment}>
        <Box>
          {isLoading && !data ? (
            <div className={classes.appointmentLoadingWrapper}>
              <span className={classes.info}>{t('loading')}</span>
            </div>
          ) : (
            <div className={classes.appointmentInfoWrapper}>
              <div className={classes.infoItem}>
                <AppointmentDetail
                  appointmentStatus={data.statusLabel}
                  startDateTime={data.startDateTime}
                  endDateTime={data.endDateTime}
                  roomFullName={data.roomFullName}
                  roomFullNameWithoutFees={data.roomFullNameWithoutFees}
                  providerName={data.providerName}
                  healthcareServiceName={data.healthcareServiceName}
                  providerId={data.providerId}
                  voiceAccessNumber={data.voiceAccessNumber}
                  organizationNumber={organization?.organizationNumber}
                />
                {relatedData.service?.attributes?.instructions && <AppointmentDetailInstruction instructions={relatedData.service?.attributes?.instructions} />}
              </div>
              {data.online && (
                <div className={classes.infoItem}>
                  <AppointmentVirtualAddress isToday={displayJoinButton} onJoinAppointment={joinOnlineAppointment} />
                </div>
              )}
              {showMap && (
                <div className={classes.infoItem} style={{ order: data.online ? 4 : 2 }}>
                  <AppointmentLocationMap
                    address={data.locationName}
                    locationAddress={data.locationAddress}
                    isOnline={data.online}
                    locationPhoneNumber={data.locationPhoneNumber}
                  />
                </div>
              )}
              {organization.features.patientPortalInvoices && (
                <div className={classes.infoItem} style={{ order: 3 }}>
                  <AppointmentInvoices invoices={data.invoicesDetail || []} id={id} />
                </div>
              )}
              <div className={classes.infoItem} style={{ order: 4 }}>
                <AppointmentAttachments files={data.filesMeta || []} id={id} attachmentUploadEnabled={data.fileUploadEnabled} />
              </div>
            </div>
          )}
        </Box>
      </div>
    </Box>
  );
}

export default AppointmentDetails;
